import { Controller } from "@hotwired/stimulus"
import { responseState, isCachedOffline } from "../field_sync_state"

// Per-response sync badge on response cards (ADR-0048 §1, W3.2).
//
// Renders into these targets:
//   offlineIcon          -- top-right of header row: cloud icon when synced + cached
//   statusBadge          -- bottom border-t row: amber/red warnings for waiting/failed/not_submitted
//   actionSlot           -- optional (card #4594): wraps the card's Start/Continue/Upcoming
//                           action. Hidden whenever a completion is queued or stuck -- the
//                           mission is either already done (just not synced) or needs
//                           attention, never re-openable from here either way (see
//                           surveys/result_editor_controller.js's own, authoritative guard).
//   completionPending    -- optional (card #4594): pre-rendered "Done, sending..." pill,
//                           shown instead of actionSlot while a completion is queued.
//   completionAttention  -- optional (card #4594): pre-rendered "Needs attention" pill,
//                           shown instead of actionSlot when a queued completion was
//                           rejected or gave up retrying.
//
// actionSlot/completionPending/completionAttention are pre-rendered, hidden-by-default
// markup from the view (no HTML built in JS for these -- only `.hidden` is toggled).
//
// Values:
//   responseId -- the server Response#id this card represents.
export default class extends Controller {
  static targets = ["offlineIcon", "statusBadge", "actionSlot", "completionPending", "completionAttention", "inProgress"]
  static values = { responseId: Number }

  connect() {
    this._render()

    this._refreshHandler = () => this._render()
    window.addEventListener("field:sync-complete", this._refreshHandler)
    window.addEventListener("field:online", this._refreshHandler)
  }

  disconnect() {
    window.removeEventListener("field:sync-complete", this._refreshHandler)
    window.removeEventListener("field:online", this._refreshHandler)
  }

  async _render() {
    const { state, waitingImages, failedImages, responsePending } =
      await responseState(this.responseIdValue)

    this._toggleAction(state)

    if (state === "synced") {
      const offline = await isCachedOffline(this.responseIdValue)
      if (offline) {
        this.offlineIconTarget.innerHTML = this._offlineReadyIcon()
        this.statusBadgeTarget.innerHTML = ""
      } else {
        this.offlineIconTarget.innerHTML = ""
        this.statusBadgeTarget.innerHTML = ""
      }
      return
    }

    // Non-synced states — clear header icon, render warning at bottom
    this.offlineIconTarget.innerHTML = ""

    let badgeClass, iconSvg, label

    if (state === "failed") {
      badgeClass = "text-red-600 bg-red-50"
      iconSvg    = this._warningIcon()
      if (failedImages > 0) {
        label = `${failedImages} ${this._plural(failedImages, "photo")} failed`
      } else {
        label = "Submission failed"
      }
    } else if (state === "completion_failed") {
      badgeClass = "text-red-600 bg-red-50"
      iconSvg    = this._warningIcon()
      label      = "Completion failed — will retry when connected"
    } else if (state === "completion_pending") {
      badgeClass = "text-emerald-600 bg-emerald-50"
      iconSvg    = this._checkIcon()
      label      = "Done, sending when you have signal"
    } else if (state === "not_submitted") {
      badgeClass = "text-amber-600 bg-amber-50"
      iconSvg    = this._clockIcon()
      label      = "Not submitted"
    } else { // waiting
      badgeClass = "text-amber-600 bg-amber-50"
      iconSvg    = this._clockIcon()
      label      = `${waitingImages} ${this._plural(waitingImages, "photo")} waiting`
    }

    this.statusBadgeTarget.innerHTML = `
      <div class="border-t border-slate-50 px-4 py-1.5">
        <span class="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeClass}">
          ${iconSvg}
          ${this._esc(label)}
        </span>
      </div>
    `
  }

  // Card #4594 -- swaps the card's action (Start/Continue/Upcoming/schedule
  // prompt) for a pre-rendered pill when a completion is queued or stuck, so
  // a finished-offline mission can't be tapped back open from the list. Only
  // runs when the view opted in by rendering actionSlot -- older/other call
  // sites of this controller without it are untouched.
  _toggleAction(state) {
    if (!this.hasActionSlotTarget) return

    const pending   = state === "completion_pending"
    const attention = state === "completion_failed"

    this.actionSlotTarget.classList.toggle("hidden", pending || attention)
    if (this.hasCompletionPendingTarget)   this.completionPendingTarget.classList.toggle("hidden", !pending)
    if (this.hasCompletionAttentionTarget) this.completionAttentionTarget.classList.toggle("hidden", !attention)
    if (this.hasInProgressTarget)          this.inProgressTarget.classList.toggle("hidden", pending || attention)
  }

  _offlineReadyIcon() {
    return `
      <svg class="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 15a4 4 0 0 0 4 4h9a5 5 0 0 0 1.82-9.64A7 7 0 1 0 3 15Z" />
      </svg>
    `
  }

  _warningIcon() {
    return `
      <svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    `
  }

  _checkIcon() {
    return `
      <svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    `
  }

  _clockIcon() {
    return `
      <svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    `
  }

  _plural(n, word) {
    return n === 1 ? word : `${word}s`
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
